import React, {useMemo, useRef} from "react";
import HelpIcon from "../Help/HelpIcon";
import {Bar} from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip as ChartTooltip,
  Legend as ChartLegend,
  TimeScale,
  ChartOptions,
  ChartData
} from "chart.js";
import "chartjs-adapter-date-fns";

import {chartHeight} from "../../constants";
import {useChartTheme} from "../../contexts/ChartThemeContext";
import {ChartErrorBoundary} from "../Charts/ChartErrorBoundary";
import {createChartDataProcessor, validateChartData} from "../../utils/chartDataProcessor";
import {alignDataToTimeline} from "../../utils/dataAlignment";
import {createTickFormatter} from "../../utils/chartTicks";

// Register Chart.js components
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, ChartTooltip, ChartLegend, TimeScale);

interface GenericValueBarChartReportProps {
  points: Array<[number, number]>; // [timestamp, value]
  title: string;
  dataSeriesKey: string;
  seriesName: string;
  color: string;
  tooltip?: React.ReactNode; // Deprecated, use tooltipId instead
  tooltipId?: string; // Help topic ID for the new help system
  axisSeries?: {
    name: string;
    data: Array<{timestamp: number; value: number}>;
    timeRange?: {start: number; end: number};
    ticks?: Array<{timestamp: number; label: string}>;
  };
  showrate?: boolean;
  durationInSeconds?: number;
}

const titleCase = (s: string) =>
  s && typeof s === "string" ? s.charAt(0).toUpperCase() + s.substr(1).toLowerCase() : "";

const GenericValueBarChartReport: React.FC<GenericValueBarChartReportProps> = (props) => {
  const chartRef = useRef<ChartJS<"bar", {x: number; y: number}[], unknown>>(null);
  const {getThemeColors} = useChartTheme();
  const dataProcessor = createChartDataProcessor();

  // Create tick formatter with consistent formatting
  // Note: Data is pre-localized in parent ChartsReport, so we pass 0 for timezone offset
  const tickFormatter = useMemo(() => {
    return createTickFormatter(0);
  }, []);

  const chartData = useMemo(() => {
    if (!props.points || props.points.length === 0) {
      // Even with no data, create empty data points for proper time axis display
      if (props.axisSeries?.data) {
        return props.axisSeries.data.map((point) => ({
          time: point.timestamp,
          timestamp: point.timestamp,
          [props.dataSeriesKey]: 0,
          hasData: false
        }));
      }
      return [];
    }

    // If we have axisSeries, use it for proper timeline alignment
    if (props.axisSeries?.data) {
      try {
        // Use 3-second windows with sum aggregation for bar charts (like errors, disconnects)
        // This matches the original pondjs implementation: sum() aggregation with 3s windows
        const alignedData = alignDataToTimeline(
          props.axisSeries.data,
          props.points, // Raw data points [timestamp, value]
          "3s", // 3 second windows (matches original)
          "sum" // Sum aggregation (matches original pondjs sum() function)
        );

        // Convert to chart format
        const chartFormatData = alignedData.map((point) => ({
          time: point.timestamp,
          timestamp: point.timestamp,
          [props.dataSeriesKey]: point.value || 0,
          hasData: point.value !== null && point.value > 0
        }));

        // Data is already optimally sampled by the preset system
        return chartFormatData;
      } catch {
        // Fall back to original method
      }
    }

    // Fallback: Original processing method (for backward compatibility)
    const validation = validateChartData(
      props.points.map((point) => ({
        timestamp: point[0],
        value: point[1]
      }))
    );

    if (!validation.isValid) {
      return [];
    }

    // Aggregate data into 3-second windows for bar chart visualization
    const aggregated = dataProcessor.aggregate(
      props.points.map((point) => ({timestamp: point[0], value: point[1]})),
      3000, // 3 seconds
      "sum"
    );

    // Transform to chart format
    const chartData = aggregated.map((item, index) => ({
      time: item.timestamp,
      timestamp: item.timestamp,
      [props.dataSeriesKey]: item.value,
      hasData: item.value > 0,
      index
    }));

    // Data is already optimally sampled by the preset system
    return chartData;
  }, [props.points, props.dataSeriesKey, props.axisSeries, dataProcessor]);

  const statistics = useMemo(() => {
    if (!props.points || props.points.length === 0) {
      return {total: 0, rate: 0, hasData: false};
    }

    const total = props.points.reduce((sum, point) => sum + point[1], 0);
    const minutes =
      props.durationInSeconds && props.durationInSeconds > 0 ? Math.round(props.durationInSeconds / 60) : 1;
    const rate = Math.round(total / minutes);

    return {total, rate, hasData: true};
  }, [props.points, props.durationInSeconds]);

  const themeColors = getThemeColors();

  // Prepare data for Chart.js
  const preparedChartData = useMemo(() => {
    return chartData
      .filter((d) => d.hasData) // Only show bars where there's actual data
      .map((d) => ({
        x: d.timestamp,
        y: d[props.dataSeriesKey] as number
      }));
  }, [chartData, props.dataSeriesKey]);

  // Chart.js data configuration
  const data: ChartData<"bar", {x: number; y: number}[]> = {
    datasets: [
      {
        label: titleCase(props.seriesName),
        data: preparedChartData,
        backgroundColor: props.color + "CC", // 80% opacity
        borderColor: props.color,
        borderWidth: 1,
        borderRadius: 4,
        barPercentage: 0.9,
        categoryPercentage: 0.8
      }
    ]
  };

  // Chart.js options configuration
  const options: ChartOptions<"bar"> = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      animation: false, // Disable animations for better performance
      interaction: {
        mode: "index" as const,
        intersect: false
      },
      onClick: (event, activeElements, chart) => {
        // Double click to reset zoom
        const now = Date.now();
        (chart as any).lastClickTime || 0;
        (chart as any).lastClickTime = now;
      },
      plugins: {
        legend: {
          display: false // We show our own label above the chart
        },
        tooltip: {
          enabled: true,
          backgroundColor: themeColors.tooltip.background,
          borderColor: themeColors.tooltip.border,
          borderWidth: 1,
          titleColor: themeColors.tooltip.text,
          bodyColor: themeColors.tooltip.text,
          padding: 12,
          displayColors: true,
          callbacks: {
            title: (context) => {
              const timestamp = context[0]?.parsed?.x;
              return timestamp ? new Date(timestamp).toLocaleTimeString() : "";
            },
            label: (context) => {
              const value = context.parsed?.y;
              return value !== null && value !== undefined
                ? `${titleCase(props.seriesName)}: ${Math.round(value)}`
                : `${titleCase(props.seriesName)}: 0`;
            }
          }
        }
      },
      scales: {
        x: {
          type: "linear",
          display: true,
          title: {
            display: false
          },
          grid: {
            display: true,
            color: themeColors.grid + "30",
            drawOnChartArea: true,
            drawTicks: true
          },
          ticks: {
            color: themeColors.text,
            font: {
              size: 11
            },
            maxTicksLimit: 20,
            autoSkip: true,
            autoSkipPadding: 10,
            callback: function (value) {
              return tickFormatter(value);
            }
          },
          min: props.axisSeries?.timeRange?.start,
          max: props.axisSeries?.timeRange?.end
        },
        y: {
          type: "linear" as const,
          display: true,
          position: "left" as const,
          title: {
            display: true,
            text: titleCase(props.seriesName),
            color: themeColors.text
          },
          grid: {
            display: true,
            color: themeColors.grid + "30",
            drawOnChartArea: true,
            drawTicks: true
          },
          ticks: {
            color: themeColors.text,
            font: {
              size: 11
            }
          },
          min: 0
        }
      }
    }),
    [themeColors, props.seriesName, props.axisSeries, tickFormatter, preparedChartData]
  );

  let label: string = "";
  if (!statistics.hasData) {
    label = `No ${props.seriesName}s`;
  } else if (props.showrate === true) {
    if (statistics.rate === 0) {
      label = "Less than 1 per minute avg";
    } else {
      label = `${statistics.rate} per minute avg`;
    }
  } else {
    label = `${statistics.total} ${props.seriesName}${statistics.total === 1 ? "" : "s"}`;
  }

  return (
    <ChartErrorBoundary>
      <div>
        <h5>
          {props.title}
          {props.tooltipId && <HelpIcon topicId={props.tooltipId} />}
        </h5>

        <div style={{marginBottom: "10px"}}>
          <div style={{display: "flex", alignItems: "center", gap: "10px"}}>
            <div
              style={{
                width: "12px",
                height: "12px",
                backgroundColor: props.color,
                borderRadius: "2px"
              }}
            ></div>
            <span style={{color: themeColors.text}}>{label}</span>
          </div>
        </div>

        <div style={{width: "100%", height: chartHeight, position: "relative"}}>
          <Bar ref={chartRef} data={data} options={options} />
        </div>
      </div>
    </ChartErrorBoundary>
  );
};

export default GenericValueBarChartReport;
